import { formatFourPlacesCurrency } from '@/shared/utils/formatMoneyDisplay';
import { addOnLineScaled, fromScaled, itemLineScaled, SCALE, toScaled } from './soldLineAmounts';
import type { SoldAddOn, SoldItem, SoldLines, SoldService } from '../types/agreements.types';
import type { InvoiceableService } from './invoiceableLines';

export type SelectionUnit =
    | { kind: 'service'; serviceId: string }
    | { kind: 'item'; serviceId: string; itemId: string }
    | { kind: 'add_on'; addOnId: string };

export interface SelectionSummaryRow {
    key: string;
    label: string;
    detail: string | null;
    net: number;
    gross: number;
}

export interface SelectionTotals {
    rows: SelectionSummaryRow[];
    net: number;
    gross: number;
}

export { SCALE, toScaled };

export const lineNet = (unitNet: number, quantity: number): number => fromScaled(toScaled(unitNet) * quantity);

/** P-D7's select-everything worst case, in ten-thousandths. bigint because the exact total
 *  can exceed Number.MAX_SAFE_INTEGER, which is exactly what the caller must detect. */
export function scaledMagnitudeOf(lines: SoldLines): bigint {
    const magnitude = (value: number, quantity = 1): bigint =>
        BigInt(toScaled(Math.abs(value))) * BigInt(quantity);
    let total = 0n;

    for (const service of lines.services) {
        total += magnitude(service.net) + magnitude(service.gross);
        for (const item of service.items) {
            const line = itemLineScaled(item);
            total += BigInt(Math.abs(line.net)) + BigInt(Math.abs(line.gross));
        }
    }
    for (const addOn of lines.addOns) {
        total += magnitude(addOn.unitNet, addOn.quantity) + magnitude(addOn.unitGross, addOn.quantity);
    }

    return total;
}

/** The one identity of a selection unit: equality, set membership and the summary
 *  row key all derive from it, so they can never disagree. */
export const unitKey = (unit: SelectionUnit): string => {
    if (unit.kind === 'service') return `service:${unit.serviceId}`;
    if (unit.kind === 'add_on') return `add_on:${unit.addOnId}`;
    return `item:${unit.serviceId}:${unit.itemId}`;
};

const unitEquals = (a: SelectionUnit, b: SelectionUnit): boolean => unitKey(a) === unitKey(b);

/** Membership index for the render path: a checkbox asks `has(unitKey(unit))` rather
 *  than scanning the selection once per cell. */
export const selectionKeySet = (selection: SelectionUnit[]): Set<string> =>
    new Set(selection.map(unitKey));

export function isUnitSelected(selection: SelectionUnit[], unit: SelectionUnit): boolean {
    const key = unitKey(unit);
    return selection.some((u) => unitKey(u) === key);
}

const findService = (lines: SoldLines, serviceId: string): SoldService | undefined =>
    lines.services.find((s) => s.id === serviceId);

const findItem = (
    lines: SoldLines,
    serviceId: string,
    itemId: string,
): { service: SoldService; item: SoldItem } | undefined => {
    const service = findService(lines, serviceId);
    const item = service?.items.find((i) => i.id === itemId);
    return service && item ? { service, item } : undefined;
};

const findAddOn = (lines: SoldLines, addOnId: string): SoldAddOn | undefined =>
    lines.addOns.find((a) => a.id === addOnId);

const linkedAddOns = (lines: SoldLines, serviceId: string): SoldAddOn[] =>
    lines.addOns.filter((addOn) => addOn.linkedServiceId === serviceId);

const addOnUnit = (addOn: SoldAddOn): SelectionUnit => ({ kind: 'add_on', addOnId: addOn.id });

function withoutUnits(units: SelectionUnit[], removed: SelectionUnit[]): SelectionUnit[] {
    const removedKeys = selectionKeySet(removed);
    return units.filter((unit) => !removedKeys.has(unitKey(unit)));
}

function withUnits(units: SelectionUnit[], added: SelectionUnit[]): SelectionUnit[] {
    const present = selectionKeySet(units);
    return [...units, ...added.filter((unit) => !present.has(unitKey(unit)))];
}

/** Single mode only, and callers pass a normalised selection, which can hold no
 *  item unit, so dropping services and their linked add-ons is sufficient. */
function withoutReplacedServices(current: SelectionUnit[], lines: SoldLines): SelectionUnit[] {
    const replaced = current.flatMap((unit) =>
        unit.kind === 'service' ? [unit, ...linkedAddOns(lines, unit.serviceId).map(addOnUnit)] : [],
    );
    return withoutUnits(current, replaced);
}

export const isPartlyInvoiced = (service: SoldService): boolean => {
    const available = service.items.filter((i) => i.status === 'available').length;
    return available > 0 && available < service.items.length;
};

/** Selectable as a whole iff it has items and every one is available (fail closed). */
export function isServiceSelectable(service: SoldService): boolean {
    return service.items.length > 0 && service.items.every((i) => i.status === 'available');
}

/** Disabled in single mode (D5), on a claimed item, on a whole-priced service (D7), or on a claim placeholder. */
export function isItemSelectable(service: SoldService, item: SoldItem, flexible: boolean): boolean {
    return flexible && item.placeholder !== true && item.status === 'available' && service.pricedAt === 'item';
}

export function isAddOnSelectable(addOn: SoldAddOn): boolean {
    return addOn.status === 'available';
}

export interface ServiceCheckbox {
    checked: boolean;
    disabled: boolean;
    note: string | null;
}

export const PARTLY_INVOICED_NOTE = 'Partly invoiced';
export const PARTLY_INVOICED_SINGLE_MODE_NOTE =
    'Partly invoiced. Remaining items can only be invoiced with flexible invoicing.';

export const availableItemUnits = (service: SoldService): SelectionUnit[] =>
    service.items
        .filter((i) => i.status === 'available')
        .map((i) => ({ kind: 'item', serviceId: service.id, itemId: i.id }));

export function serviceCheckbox(
    service: InvoiceableService,
    selectedKeys: ReadonlySet<string>,
    flexible: boolean,
): ServiceCheckbox {
    if (!service.partlyInvoiced) {
        return {
            checked: selectedKeys.has(unitKey({ kind: 'service', serviceId: service.id })),
            disabled: !isServiceSelectable(service),
            note: null,
        };
    }

    return {
        checked: availableItemUnits(service).every((unit) => selectedKeys.has(unitKey(unit))),
        disabled: !flexible || service.pricedAt !== 'item',
        note: flexible ? PARTLY_INVOICED_NOTE : PARTLY_INVOICED_SINGLE_MODE_NOTE,
    };
}

function togglePartlyInvoiced(
    current: SelectionUnit[],
    service: SoldService,
    lines: SoldLines,
    flexible: boolean,
): SelectionUnit[] {
    if (!flexible || service.pricedAt !== 'item') return current;

    const itemUnits = availableItemUnits(service);
    const addOnUnits = linkedAddOns(lines, service.id).filter(isAddOnSelectable).map(addOnUnit);
    const selected = selectionKeySet(current);
    const everyItemSelected = itemUnits.every((unit) => selected.has(unitKey(unit)));

    return everyItemSelected
        ? withoutUnits(current, [...itemUnits, ...addOnUnits])
        : withUnits(current, [...itemUnits, ...addOnUnits]);
}

function promotesToService(service: SoldService, selection: SelectionUnit[]): boolean {
    if (!isServiceSelectable(service) || service.pricedAt !== 'item') return false;

    const selected = selectionKeySet(selection);
    return service.items.every((item) =>
        selected.has(unitKey({ kind: 'item', serviceId: service.id, itemId: item.id })),
    );
}

export function toggleSelection(
    current: SelectionUnit[],
    unit: SelectionUnit,
    lines: SoldLines,
    flexible: boolean,
): SelectionUnit[] {
    if (unit.kind === 'service') {
        const service = findService(lines, unit.serviceId);
        if (service && isPartlyInvoiced(service)) {
            return togglePartlyInvoiced(current, service, lines, flexible);
        }
        if (!service || !isServiceSelectable(service)) return current; // T1, T2

        const linked = linkedAddOns(lines, unit.serviceId);
        if (isUnitSelected(current, unit)) {
            return withoutUnits(current, [unit, ...linked.map(addOnUnit)]); // T6, T8, T18
        }

        const kept = flexible
            ? current.filter((u) => !(u.kind === 'item' && u.serviceId === unit.serviceId)) // T9
            : withoutReplacedServices(current, lines); // T7, T19
        return withUnits([...kept, unit], linked.filter(isAddOnSelectable).map(addOnUnit)); // T16, T21
    }

    if (unit.kind === 'item') {
        const found = findItem(lines, unit.serviceId, unit.itemId);
        if (!found) return current; // T1
        if (!isItemSelectable(found.service, found.item, flexible)) return current; // T3, T5, T10

        const serviceUnit: SelectionUnit = { kind: 'service', serviceId: unit.serviceId };
        if (isUnitSelected(current, serviceUnit)) {
            const otherAvailableItems: SelectionUnit[] = found.service.items
                .filter((i) => i.id !== unit.itemId && i.status === 'available')
                .map((i) => ({ kind: 'item', serviceId: unit.serviceId, itemId: i.id }));
            return [...current.filter((u) => !unitEquals(u, serviceUnit)), ...otherAvailableItems]; // T11
        }

        if (isUnitSelected(current, unit)) {
            return current.filter((u) => !unitEquals(u, unit)); // T12
        }

        const added = [...current, unit];
        return promotesToService(found.service, added)
            ? [...added.filter((u) => !(u.kind === 'item' && u.serviceId === unit.serviceId)), serviceUnit] // T13
            : added;
    }

    const addOn = findAddOn(lines, unit.addOnId);
    if (!addOn || !isAddOnSelectable(addOn)) return current; // T1, T4

    return isUnitSelected(current, unit)
        ? current.filter((u) => !unitEquals(u, unit))
        : [...current, unit]; // T14
}

/** Every unit the current lines still allow, walked once, so normalisation costs
 *  one pass over the lines instead of a scan per selected unit. */
function selectableUnitKeys(lines: SoldLines, flexible: boolean): Set<string> {
    const keys = new Set<string>();

    for (const service of lines.services) {
        if (isServiceSelectable(service)) {
            keys.add(unitKey({ kind: 'service', serviceId: service.id }));
        }
        for (const item of service.items) {
            if (isItemSelectable(service, item, flexible)) {
                keys.add(unitKey({ kind: 'item', serviceId: service.id, itemId: item.id }));
            }
        }
    }
    for (const addOn of lines.addOns) {
        if (isAddOnSelectable(addOn)) keys.add(unitKey({ kind: 'add_on', addOnId: addOn.id }));
    }

    return keys;
}

/** Drops units whose line is missing or no longer selectable, then (single mode)
 *  keeps the first remaining service and every remaining add-on. */
export function normaliseSelection(
    selection: SelectionUnit[],
    lines: SoldLines | undefined,
    flexible: boolean,
): SelectionUnit[] {
    if (!lines) return [];

    const selectable = selectableUnitKeys(lines, flexible);
    const valid = selection.filter((unit) => selectable.has(unitKey(unit)));
    if (flexible) return valid;

    const firstService = valid.find((unit) => unit.kind === 'service');
    const firstServiceKey = firstService ? unitKey(firstService) : null;
    return valid.filter((unit) => unit.kind === 'add_on' || unitKey(unit) === firstServiceKey);
}

/** Indexed once per call: a linear scan per sold line would be quadratic at the
 *  API's 200-services-by-200-items cap. */
export function selectionTotals(selection: SelectionUnit[], lines: SoldLines): SelectionTotals {
    const selectedKeys = selectionKeySet(selection);
    const rows: SelectionSummaryRow[] = [];
    let netScaled = 0;
    let grossScaled = 0;

    for (const service of lines.services) {
        const serviceKey = unitKey({ kind: 'service', serviceId: service.id });
        if (selectedKeys.has(serviceKey)) {
            const scaledNet = toScaled(service.net);
            const scaledGross = toScaled(service.gross);
            netScaled += scaledNet;
            grossScaled += scaledGross;
            rows.push({
                key: serviceKey,
                label: service.name,
                detail: null,
                net: fromScaled(scaledNet),
                gross: fromScaled(scaledGross),
            });
            continue;
        }

        for (const item of service.items) {
            const itemKey = unitKey({ kind: 'item', serviceId: service.id, itemId: item.id });
            if (!selectedKeys.has(itemKey)) continue;

            const { net: scaledNet, gross: scaledGross } = itemLineScaled(item);
            netScaled += scaledNet;
            grossScaled += scaledGross;
            rows.push({
                key: itemKey,
                label: item.name,
                detail: `${item.quantity} x ${formatFourPlacesCurrency(item.unitNet.toFixed(4))}`,
                net: fromScaled(scaledNet),
                gross: fromScaled(scaledGross),
            });
        }
    }

    for (const addOn of lines.addOns) {
        const addOnKey = unitKey({ kind: 'add_on', addOnId: addOn.id });
        if (!selectedKeys.has(addOnKey)) continue;

        const { net: scaledNet, gross: scaledGross } = addOnLineScaled(addOn);
        netScaled += scaledNet;
        grossScaled += scaledGross;
        rows.push({
            key: addOnKey,
            label: addOn.name,
            detail: `${addOn.quantity} x ${formatFourPlacesCurrency(addOn.unitNet.toFixed(4))}`,
            net: fromScaled(scaledNet),
            gross: fromScaled(scaledGross),
        });
    }

    return { rows, net: fromScaled(netScaled), gross: fromScaled(grossScaled) };
}
